/**
 * LumeroFlow Help Centre Content
 * 
 * This file contains all help articles, categories, and related data.
 * Structure is designed to be easily migrated to a CMS or backend later.
 */

export const HELP_CATEGORIES = [
  { id: "getting-started", name: "Getting started", icon: "Rocket", description: "Set up your clinic and get started with LumeroFlow" },
  { id: "calendar", name: "Calendar & Appointments", icon: "CalendarDays", description: "Book, manage, and track appointments" },
  { id: "online-booking", name: "Online booking", icon: "Globe", description: "Let patients book appointments online" },
  { id: "forms", name: "Forms & Consent", icon: "FileCheck", description: "Create forms, collect signatures, and manage consent" },
  { id: "patients", name: "Patients", icon: "Users", description: "Manage patient records and history" },
  { id: "notes", name: "Treatment notes", icon: "ClipboardList", description: "Document treatments and clinical notes" },
  { id: "invoices", name: "Invoices & Payments", icon: "Receipt", description: "Create invoices and record payments" },
  { id: "settings", name: "Settings", icon: "Settings", description: "Configure your clinic settings" },
  { id: "troubleshooting", name: "Troubleshooting", icon: "AlertCircle", description: "Solve common issues" },
];

export const HELP_ARTICLES = [
  // ────────────────────────────────────────────────────────────────
  // GETTING STARTED
  // ────────────────────────────────────────────────────────────────
  {
    id: "setup-clinic-profile",
    title: "How to set up your clinic profile",
    category: "getting-started",
    keywords: ["clinic", "profile", "setup", "name", "address", "logo", "branding"],
    summary: "Configure your clinic name, address, contact details, and branding.",
    steps: [
      "Go to Settings from the sidebar.",
      "Click the Clinic tab.",
      "Enter your clinic name, address, phone number, and email.",
      "Upload your clinic logo (appears on invoices and documents).",
      "Click Save changes."
    ],
    tips: [
      "Use a high-quality logo image for best results on invoices.",
      "Keep your clinic address accurate for patient communications."
    ],
    relatedArticleIds: ["add-practitioners", "set-availability"],
    roles: ["owner"]
  },
  {
    id: "add-practitioners",
    title: "How to add practitioners",
    category: "getting-started",
    keywords: ["practitioner", "staff", "team", "add", "invite", "user"],
    summary: "Add practitioners to your clinic so they can manage appointments and notes.",
    steps: [
      "Go to Team from the sidebar.",
      "Click Invite team member.",
      "Enter the practitioner's name, email, and role.",
      "Choose their role: Owner, Practitioner, or Receptionist.",
      "Send the invitation."
    ],
    tips: [
      "Practitioners can view and manage their own appointments.",
      "Owners have full access to billing and settings."
    ],
    relatedArticleIds: ["set-availability", "setup-clinic-profile"],
    roles: ["owner"]
  },
  {
    id: "add-appointment-types",
    title: "How to add appointment types",
    category: "getting-started",
    keywords: ["appointment type", "service", "duration", "price", "initial", "follow-up"],
    summary: "Create appointment types like Initial Consultation, Follow-up, or specific treatments.",
    steps: [
      "Go to Settings from the sidebar.",
      "Click the Appointment Types tab.",
      "Click Add appointment type.",
      "Enter a name (e.g., Initial Consultation), duration, and optional price.",
      "Choose a colour for calendar display.",
      "Enable or disable online booking for this type.",
      "Click Save."
    ],
    tips: [
      "Use different colours to quickly identify appointment types on the calendar.",
      "Set realistic durations including buffer time for notes."
    ],
    relatedArticleIds: ["set-availability", "enable-online-booking"],
    roles: ["owner"]
  },
  {
    id: "set-availability",
    title: "How to set practitioner availability",
    category: "getting-started",
    keywords: ["availability", "schedule", "hours", "working", "time", "slots"],
    summary: "Define when each practitioner is available for appointments.",
    steps: [
      "Go to Settings from the sidebar.",
      "Click the Availability tab.",
      "Select a practitioner from the dropdown.",
      "For each day of the week, set the start and end times.",
      "Toggle days off by clicking the day checkbox.",
      "Click Save availability."
    ],
    tips: [
      "Online booking will only show slots within availability windows.",
      "You can set different availability for different practitioners."
    ],
    screenshotId: "availability-setup",
    relatedArticleIds: ["enable-online-booking", "add-practitioners"],
    roles: ["owner"]
  },
  {
    id: "enable-online-booking",
    title: "How to enable online booking",
    category: "getting-started",
    keywords: ["online", "booking", "website", "public", "link", "patients"],
    summary: "Allow patients to book appointments directly from your website.",
    steps: [
      "Go to Settings from the sidebar.",
      "Click the Online Booking tab.",
      "Toggle Enable online booking to ON.",
      "Choose which appointment types are available online.",
      "Choose which practitioners accept online bookings.",
      "Copy your booking link to share with patients.",
      "Optionally copy the iframe embed code for your website."
    ],
    tips: [
      "Test your booking page before sharing the link.",
      "Only appointment types marked for online booking will appear."
    ],
    screenshotId: "online-booking-settings",
    relatedArticleIds: ["embed-booking-page", "set-availability"],
    roles: ["owner"]
  },

  // ────────────────────────────────────────────────────────────────
  // CALENDAR & APPOINTMENTS
  // ────────────────────────────────────────────────────────────────
  {
    id: "book-appointment",
    title: "How to book an appointment",
    category: "calendar",
    keywords: ["book", "appointment", "schedule", "calendar", "patient"],
    summary: "Book a new appointment for a patient from the calendar.",
    steps: [
      "Go to Calendar from the sidebar.",
      "Click an empty time slot on the calendar.",
      "Search for an existing patient or add a new one.",
      "Select the appointment type.",
      "Choose the practitioner and confirm the date/time.",
      "Click Book appointment."
    ],
    tips: [
      "You can also use the + Book button at the top of the calendar.",
      "Double-click an empty slot for quick booking."
    ],
    screenshotId: "calendar-booking",
    relatedArticleIds: ["edit-appointment", "cancel-appointment", "use-appointment-drawer"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "edit-appointment",
    title: "How to edit or reschedule an appointment",
    category: "calendar",
    keywords: ["edit", "reschedule", "change", "appointment", "time", "date"],
    summary: "Change the time, date, or details of an existing appointment.",
    steps: [
      "Click the appointment on the calendar to open the drawer.",
      "Click the Edit or Reschedule button.",
      "Change the date, time, or appointment type as needed.",
      "Click Save changes."
    ],
    tips: [
      "Rescheduled appointments update the patient timeline automatically.",
      "Consider sending a confirmation to the patient after rescheduling."
    ],
    relatedArticleIds: ["book-appointment", "cancel-appointment"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "cancel-appointment",
    title: "How to cancel an appointment",
    category: "calendar",
    keywords: ["cancel", "delete", "appointment", "no-show"],
    summary: "Cancel or mark an appointment as no-show.",
    steps: [
      "Click the appointment on the calendar to open the drawer.",
      "Click Cancel appointment or Mark as no-show.",
      "Confirm the cancellation.",
      "The appointment status will update accordingly."
    ],
    tips: [
      "Cancelled appointments remain in the patient history for reference.",
      "No-shows can be tracked in reports."
    ],
    relatedArticleIds: ["book-appointment", "edit-appointment"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "use-appointment-drawer",
    title: "How to use the appointment drawer",
    category: "calendar",
    keywords: ["drawer", "sidebar", "appointment", "details", "forms", "notes"],
    summary: "Access patient info, forms, notes, and actions from the appointment drawer.",
    steps: [
      "Click any appointment on the calendar.",
      "The drawer opens on the right showing patient details.",
      "See Patient Forms section to manage consent forms.",
      "Click Start Notes to create a treatment note.",
      "Use action buttons: Arrived, Completed, Cancel, etc."
    ],
    tips: [
      "The drawer provides quick access without leaving the calendar.",
      "Click the patient name to open their full profile."
    ],
    screenshotId: "appointment-drawer",
    relatedArticleIds: ["complete-form-on-device", "create-treatment-note"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "privacy-mode",
    title: "How to hide patient names (privacy mode)",
    category: "calendar",
    keywords: ["privacy", "hide", "names", "confidential", "screen"],
    summary: "Hide patient names on the calendar when screen is visible to others.",
    steps: [
      "Look for the eye icon at the top of the calendar.",
      "Click to toggle privacy mode ON.",
      "Patient names will be replaced with appointment type only.",
      "Click again to show names."
    ],
    tips: [
      "Use privacy mode when your screen is visible in a waiting area.",
      "Names are still visible when you click to open the appointment."
    ],
    relatedArticleIds: ["use-appointment-drawer"],
    roles: ["owner", "practitioner", "receptionist"]
  },

  // ────────────────────────────────────────────────────────────────
  // ONLINE BOOKING
  // ────────────────────────────────────────────────────────────────
  {
    id: "embed-booking-page",
    title: "How to embed the booking page on your website",
    category: "online-booking",
    keywords: ["embed", "iframe", "website", "booking", "widget", "integration"],
    summary: "Add your LumeroFlow booking page directly into your clinic website.",
    steps: [
      "Go to Settings > Online Booking.",
      "Scroll to the Embed on your website section.",
      "Copy the iframe embed code provided.",
      "Paste the code into your website HTML where you want the booking widget.",
      "The booking form will appear inline on your website."
    ],
    tips: [
      "You can also share the direct booking link instead of embedding.",
      "Test the embed on your website before going live."
    ],
    screenshotId: "embed-code",
    relatedArticleIds: ["enable-online-booking", "troubleshoot-no-slots"],
    roles: ["owner"]
  },
  {
    id: "choose-online-appointment-types",
    title: "How to choose which services are available online",
    category: "online-booking",
    keywords: ["appointment types", "services", "online", "available", "disable"],
    summary: "Control which appointment types patients can book online.",
    steps: [
      "Go to Settings > Online Booking.",
      "In the Available appointment types section, toggle each type ON or OFF.",
      "Only enabled types will appear on your public booking page.",
      "Click Save changes."
    ],
    tips: [
      "Keep initial consultations online, disable complex treatments that need discussion.",
      "Review this periodically as your services change."
    ],
    relatedArticleIds: ["enable-online-booking", "add-appointment-types"],
    roles: ["owner"]
  },

  // ────────────────────────────────────────────────────────────────
  // FORMS & CONSENT
  // ────────────────────────────────────────────────────────────────
  {
    id: "create-form",
    title: "How to create a form",
    category: "forms",
    keywords: ["form", "create", "template", "fields", "consent", "intake"],
    summary: "Build custom intake forms, consent forms, or questionnaires.",
    steps: [
      "Go to Forms from the sidebar.",
      "Click Create form.",
      "Add a form name and description.",
      "Drag and drop fields: text, date, checkboxes, signature, etc.",
      "Mark fields as required if needed.",
      "Click Save template."
    ],
    tips: [
      "Use AI Generate to create a form from a description.",
      "Preview your form before assigning it to appointments."
    ],
    relatedArticleIds: ["upload-pdf-form", "assign-forms"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "upload-pdf-form",
    title: "How to upload a PDF form",
    category: "forms",
    keywords: ["pdf", "upload", "document", "existing", "consent"],
    summary: "Upload an existing clinic PDF form for patients to view, acknowledge, and sign.",
    steps: [
      "Go to Forms from the sidebar.",
      "Click Upload form.",
      "Drag and drop or browse to select your PDF file.",
      "Enter a form name and description.",
      "Configure acknowledgement text and consent checkbox if needed.",
      "Set assignment rules (all appointments, specific types, etc.).",
      "Click Upload form."
    ],
    tips: [
      "PDF forms are displayed to patients with acknowledgement checkboxes and signature.",
      "Maximum file size is 25MB."
    ],
    relatedArticleIds: ["create-form", "assign-forms", "complete-form-on-device"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "assign-forms",
    title: "How to assign forms to appointment types",
    category: "forms",
    keywords: ["assign", "forms", "appointment", "types", "automatic", "required"],
    summary: "Make forms appear automatically for specific appointment types or all appointments.",
    steps: [
      "Go to Forms from the sidebar.",
      "Find the form you want to assign.",
      "Click Edit (or view assignment rules for PDF forms).",
      "Enable assignment rules: All appointments, New patients, specific types, etc.",
      "Mark the form as Active.",
      "Save the form."
    ],
    tips: [
      "Forms only appear on appointments if they are Active.",
      "Inactive forms stay in your library but don't get assigned."
    ],
    screenshotId: "form-assignment",
    relatedArticleIds: ["create-form", "complete-form-on-device", "troubleshoot-forms-not-appearing"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "complete-form-on-device",
    title: "How to complete a form on a clinic device",
    category: "forms",
    keywords: ["device", "tablet", "iPad", "patient", "sign", "complete", "consent"],
    summary: "Hand your tablet or device to a patient to complete and sign their form.",
    steps: [
      "Open the appointment drawer from the calendar.",
      "In the Patient Forms section, find the pending form.",
      "Click Complete on the form you want the patient to fill.",
      "Hand the device to the patient.",
      "The patient fills out the form and signs.",
      "The patient taps Submit form.",
      "Take the device back. The form is now marked as Submitted."
    ],
    tips: [
      "The patient-facing mode hides staff controls for privacy.",
      "Date signed is recorded automatically using clinic timezone."
    ],
    screenshotId: "device-form-mode",
    relatedArticleIds: ["understand-signatures", "view-submitted-forms"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "understand-signatures",
    title: "How drawn signatures and date signed work",
    category: "forms",
    keywords: ["signature", "sign", "draw", "type", "date", "consent", "legal"],
    summary: "Understand how LumeroFlow captures and stores patient signatures.",
    steps: [
      "Patients can draw their signature on the touchscreen or type their name.",
      "Drawn signatures are stored as images.",
      "Typed signatures are stored with the name entered.",
      "Date signed is automatically recorded when the form is submitted.",
      "The date uses your clinic's timezone (Europe/London by default).",
      "Signature audit data includes timestamp, IP address, and device info."
    ],
    tips: [
      "Patients cannot change the Date signed - it's recorded at submission.",
      "Both drawn and typed signatures are legally valid electronic signatures."
    ],
    relatedArticleIds: ["complete-form-on-device", "view-submitted-forms"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "view-submitted-forms",
    title: "How to view submitted forms",
    category: "forms",
    keywords: ["view", "submitted", "forms", "signature", "audit", "history"],
    summary: "Review forms that patients have completed and signed.",
    steps: [
      "Open the appointment drawer from the calendar.",
      "In the Patient Forms section, find forms marked as Submitted.",
      "Click View to open the submission.",
      "See all responses, signature, date signed, and audit trail.",
      "For PDF forms, view the original document plus acknowledgements."
    ],
    tips: [
      "Submitted forms cannot be edited by patients.",
      "The audit trail shows when and where the form was signed."
    ],
    screenshotId: "submission-viewer",
    relatedArticleIds: ["complete-form-on-device", "understand-signatures"],
    roles: ["owner", "practitioner", "receptionist"]
  },

  // ────────────────────────────────────────────────────────────────
  // PATIENTS
  // ────────────────────────────────────────────────────────────────
  {
    id: "add-patient",
    title: "How to add a patient",
    category: "patients",
    keywords: ["add", "patient", "new", "create", "register"],
    summary: "Add a new patient to your clinic records.",
    steps: [
      "Go to Patients from the sidebar.",
      "Click Add patient.",
      "Enter first name, last name, date of birth.",
      "Add email and phone number.",
      "Add any notes or address details.",
      "Click Save patient."
    ],
    tips: [
      "Patients can also be created during booking if they don't exist.",
      "Email is required if you want to send online booking confirmations."
    ],
    relatedArticleIds: ["search-patients", "view-patient-profile"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "view-patient-profile",
    title: "How to view a patient profile",
    category: "patients",
    keywords: ["profile", "patient", "history", "appointments", "notes", "files"],
    summary: "Access a patient's complete history including appointments, notes, forms, and invoices.",
    steps: [
      "Go to Patients and search for the patient.",
      "Click the patient row to open their profile.",
      "Use tabs to view: Overview, Appointments, Notes, Forms, Files, Invoices.",
      "Click items to view details."
    ],
    tips: [
      "The timeline shows all patient interactions in chronological order.",
      "You can edit patient details from the profile page."
    ],
    relatedArticleIds: ["add-patient", "search-patients"],
    roles: ["owner", "practitioner", "receptionist"]
  },
  {
    id: "search-patients",
    title: "How to search for patients",
    category: "patients",
    keywords: ["search", "find", "patient", "lookup"],
    summary: "Quickly find a patient by name, email, or phone number.",
    steps: [
      "Go to Patients from the sidebar.",
      "Use the search bar at the top.",
      "Type the patient's name, email, or phone number.",
      "Results filter as you type.",
      "Click a patient to open their profile."
    ],
    tips: [
      "Search works across first name, last name, and contact details.",
      "Use the calendar search to find patients by appointment."
    ],
    relatedArticleIds: ["add-patient", "view-patient-profile"],
    roles: ["owner", "practitioner", "receptionist"]
  },

  // ────────────────────────────────────────────────────────────────
  // TREATMENT NOTES
  // ────────────────────────────────────────────────────────────────
  {
    id: "create-treatment-note",
    title: "How to create a treatment note",
    category: "notes",
    keywords: ["note", "treatment", "clinical", "documentation", "SOAP"],
    summary: "Document the treatment provided during an appointment.",
    steps: [
      "Open the appointment drawer from the calendar.",
      "Click Start Notes or go to the patient profile > Notes tab.",
      "Select a note template if available.",
      "Fill in the clinical fields: subjective, objective, assessment, plan.",
      "Add body charts if needed.",
      "Click Save draft to continue later, or Save as final when complete."
    ],
    tips: [
      "Draft notes can be edited. Final notes are locked.",
      "Use Copy previous note for follow-up visits."
    ],
    relatedArticleIds: ["use-ai-documentation", "copy-previous-note"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "use-ai-documentation",
    title: "How to use AI-assisted documentation",
    category: "notes",
    keywords: ["AI", "transcription", "recording", "voice", "documentation", "automatic"],
    summary: "Record your consultation and let AI generate structured notes.",
    steps: [
      "Open the note editor for an appointment.",
      "Click the microphone/record button to start recording.",
      "Conduct your consultation as normal.",
      "Click Stop when finished.",
      "AI will transcribe and structure the notes.",
      "Review and edit the AI-generated content.",
      "Save as draft or final."
    ],
    tips: [
      "Speak clearly and mention key clinical terms.",
      "Always review AI-generated notes before finalising."
    ],
    screenshotId: "ai-documentation",
    relatedArticleIds: ["create-treatment-note", "troubleshoot-ai-recording"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "copy-previous-note",
    title: "How to copy a previous note",
    category: "notes",
    keywords: ["copy", "previous", "note", "follow-up", "template"],
    summary: "Pre-fill a new note from the patient's last treatment note.",
    steps: [
      "Open the note editor for a follow-up appointment.",
      "Click Copy previous note at the top.",
      "The previous note content is copied into the new note.",
      "Edit and update as needed for this visit.",
      "Save the note."
    ],
    tips: [
      "This is ideal for follow-up appointments with similar treatments.",
      "Always update copied content to reflect the current visit."
    ],
    relatedArticleIds: ["create-treatment-note"],
    roles: ["owner", "practitioner"]
  },

  // ────────────────────────────────────────────────────────────────
  // INVOICES & PAYMENTS
  // ────────────────────────────────────────────────────────────────
  {
    id: "create-invoice",
    title: "How to create an invoice",
    category: "invoices",
    keywords: ["invoice", "billing", "charge", "payment", "receipt"],
    summary: "Generate an invoice for services provided.",
    steps: [
      "Go to Invoices from the sidebar.",
      "Click Create invoice.",
      "Select the patient.",
      "Add line items: completed appointments or products.",
      "Review the total.",
      "Click Create invoice."
    ],
    tips: [
      "Invoices can be created directly from the appointment drawer too.",
      "Your clinic logo appears on invoice PDFs automatically."
    ],
    relatedArticleIds: ["record-payment", "download-invoice-pdf"],
    roles: ["owner", "receptionist"]
  },
  {
    id: "record-payment",
    title: "How to record a payment",
    category: "invoices",
    keywords: ["payment", "paid", "cash", "card", "record", "receipt"],
    summary: "Mark an invoice as paid and record the payment method.",
    steps: [
      "Open the invoice you want to mark as paid.",
      "Click Record payment or Mark as paid.",
      "Enter the payment amount.",
      "Select the payment method: Cash, Card, Bank Transfer, etc.",
      "Click Record payment.",
      "The invoice status updates to Paid."
    ],
    tips: [
      "Partial payments are supported - record the amount received.",
      "Payment history is tracked on the invoice."
    ],
    screenshotId: "record-payment",
    relatedArticleIds: ["create-invoice", "view-payment-history"],
    roles: ["owner", "receptionist"]
  },
  {
    id: "mark-appointment-paid",
    title: "How to mark an appointment as paid quickly",
    category: "invoices",
    keywords: ["quick", "pay", "appointment", "paid", "button"],
    summary: "Use the quick pay button in the appointment drawer.",
    steps: [
      "Open the appointment drawer from the calendar.",
      "Look for the Quick Pay or Mark as Paid button.",
      "Click it to open the payment modal.",
      "Select the payment method.",
      "Confirm the payment.",
      "An invoice and payment record are created automatically."
    ],
    tips: [
      "This is faster than creating an invoice manually.",
      "The invoice is linked to the appointment automatically."
    ],
    relatedArticleIds: ["create-invoice", "record-payment"],
    roles: ["owner", "receptionist"]
  },

  // ────────────────────────────────────────────────────────────────
  // SETTINGS
  // ────────────────────────────────────────────────────────────────
  {
    id: "clinic-settings",
    title: "How to configure clinic settings",
    category: "settings",
    keywords: ["settings", "clinic", "configuration", "preferences"],
    summary: "Overview of all clinic settings and where to find them.",
    steps: [
      "Go to Settings from the sidebar.",
      "Clinic tab: Name, address, logo, contact details.",
      "Appointment Types tab: Services your clinic offers.",
      "Availability tab: Practitioner working hours.",
      "Online Booking tab: Public booking page settings.",
      "Preferences tab: Invoice prefix, currency, tax settings."
    ],
    tips: [
      "Changes to settings take effect immediately.",
      "Only clinic owners can modify most settings."
    ],
    relatedArticleIds: ["setup-clinic-profile", "set-availability", "enable-online-booking"],
    roles: ["owner"]
  },

  // ────────────────────────────────────────────────────────────────
  // TROUBLESHOOTING
  // ────────────────────────────────────────────────────────────────
  {
    id: "troubleshoot-no-slots",
    title: "Troubleshooting: No online booking slots showing",
    category: "troubleshooting",
    keywords: ["no slots", "booking", "empty", "availability", "problem"],
    summary: "Why patients might not see available appointment slots.",
    steps: [
      "Check that Online Booking is enabled in Settings > Online Booking.",
      "Verify practitioner availability is set in Settings > Availability.",
      "Ensure at least one appointment type is enabled for online booking.",
      "Check that the practitioner is marked as available for online bookings.",
      "Verify the appointment type duration fits within availability windows.",
      "Check for existing appointments that might be blocking slots."
    ],
    tips: [
      "Use the booking page preview to see what patients see.",
      "Slots only appear within the configured lead time (e.g., next 2 weeks)."
    ],
    relatedArticleIds: ["set-availability", "enable-online-booking"],
    roles: ["owner"]
  },
  {
    id: "troubleshoot-forms-not-appearing",
    title: "Troubleshooting: Forms not appearing on appointment",
    category: "troubleshooting",
    keywords: ["forms", "missing", "not showing", "appointment", "patient forms"],
    summary: "Why a form might not appear in the appointment drawer.",
    steps: [
      "Check that the form is marked as Active in the Forms section.",
      "Verify the form's assignment rules match the appointment type.",
      "Check if 'Applies to all appointments' is enabled, or the specific type is selected.",
      "Ensure the form hasn't already been submitted for this appointment.",
      "Refresh the appointment drawer after making changes."
    ],
    tips: [
      "Inactive forms don't appear on any appointments.",
      "Already submitted forms show as 'Submitted', not 'Pending'."
    ],
    relatedArticleIds: ["assign-forms", "complete-form-on-device"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "troubleshoot-ai-recording",
    title: "Troubleshooting: AI recording not working",
    category: "troubleshooting",
    keywords: ["AI", "recording", "microphone", "transcription", "failed", "error"],
    summary: "Solve issues with AI-assisted documentation recording.",
    steps: [
      "Check that your browser has microphone permission for LumeroFlow.",
      "Ensure your microphone is working (test in other apps).",
      "Check your internet connection - AI requires online access.",
      "Verify your OpenAI API key is configured in Settings (if required).",
      "Try refreshing the page and starting a new recording.",
      "Check browser console for error messages."
    ],
    tips: [
      "Chrome and Edge work best for audio recording.",
      "Quiet environments produce better transcription results."
    ],
    relatedArticleIds: ["use-ai-documentation"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "troubleshoot-pdf-upload",
    title: "Troubleshooting: PDF upload problems",
    category: "troubleshooting",
    keywords: ["PDF", "upload", "failed", "error", "file", "size"],
    summary: "Solve issues when uploading PDF forms.",
    steps: [
      "Check that your file is a valid PDF (not Word doc or image).",
      "Verify the file size is under 25MB.",
      "Ensure the PDF is not password-protected.",
      "Try a different browser if the upload keeps failing.",
      "Check your internet connection.",
      "Contact support if the issue persists."
    ],
    tips: [
      "Compress large PDFs before uploading.",
      "Scanned documents should be clear and readable."
    ],
    relatedArticleIds: ["upload-pdf-form"],
    roles: ["owner", "practitioner"]
  },
  {
    id: "troubleshoot-email-not-received",
    title: "Troubleshooting: Patient did not receive email",
    category: "troubleshooting",
    keywords: ["email", "not received", "confirmation", "booking", "notification"],
    summary: "Why a patient might not have received an email notification.",
    steps: [
      "Verify the patient's email address is correct in their profile.",
      "Ask the patient to check their spam/junk folder.",
      "Note: Email sending is not yet fully integrated - confirmations may be pending.",
      "Check if the patient has opted out of communications.",
      "Contact support if emails consistently fail to send."
    ],
    tips: [
      "Email integration will be expanded in future updates.",
      "For now, you may need to confirm appointments manually via phone."
    ],
    relatedArticleIds: ["add-patient"],
    roles: ["owner", "receptionist"]
  },
];

// Screenshot annotations for key articles - Real LumeroFlow UI screenshots
export const HELP_SCREENSHOTS = {
  "availability-setup": {
    url: "/help/screenshots/availability-settings.png",
    alt: "Practitioner availability settings in LumeroFlow",
    annotations: [
      { id: 1, label: "Team tab", x: 25, y: 19 },
      { id: 2, label: "Practitioner list", x: 40, y: 45 },
      { id: 3, label: "Edit availability button", x: 85, y: 35 },
    ]
  },
  "online-booking-settings": {
    url: "/help/screenshots/online-booking-settings.png",
    alt: "Online booking settings in LumeroFlow",
    annotations: [
      { id: 1, label: "Enable toggle", x: 85, y: 28 },
      { id: 2, label: "Booking URL", x: 45, y: 42 },
      { id: 3, label: "Copy link buttons", x: 85, y: 42 },
    ]
  },
  "appointment-drawer": {
    url: "/help/screenshots/calendar-appointment-drawer.png",
    alt: "Appointment drawer in LumeroFlow calendar",
    annotations: [
      { id: 1, label: "Click appointment", x: 48, y: 40 },
      { id: 2, label: "Patient details", x: 82, y: 15 },
      { id: 3, label: "Patient Forms section", x: 82, y: 35 },
      { id: 4, label: "Treatment note", x: 82, y: 55 },
      { id: 5, label: "Action buttons", x: 82, y: 75 },
    ]
  },
  "form-assignment": {
    url: "/help/screenshots/forms-library.png",
    alt: "Forms library in LumeroFlow",
    annotations: [
      { id: 1, label: "Upload form button", x: 78, y: 12 },
      { id: 2, label: "Create form button", x: 91, y: 12 },
      { id: 3, label: "Form cards", x: 45, y: 40 },
      { id: 4, label: "Edit/Preview actions", x: 45, y: 55 },
    ]
  },
  "device-form-mode": {
    url: "/help/screenshots/complete-form-on-device.png",
    alt: "Patient completing consent form on device in LumeroFlow",
    annotations: [
      { id: 1, label: "Form title and patient", x: 50, y: 12 },
      { id: 2, label: "Form fields", x: 50, y: 60 },
      { id: 3, label: "Submit form button", x: 50, y: 92 },
    ]
  },
  "submission-viewer": {
    url: "/help/screenshots/submitted-form-viewer.png",
    alt: "Viewing a submitted form in LumeroFlow",
    annotations: [
      { id: 1, label: "Submission date", x: 45, y: 18 },
      { id: 2, label: "Signature type badge", x: 35, y: 22 },
      { id: 3, label: "Form content", x: 50, y: 50 },
      { id: 4, label: "Export PDF button", x: 92, y: 4 },
    ]
  },
  "ai-documentation": {
    url: "/help/screenshots/ai-documentation-flow.png",
    alt: "AI-assisted documentation in LumeroFlow",
    annotations: [
      { id: 1, label: "Start recording button", x: 58, y: 22 },
      { id: 2, label: "Template selector", x: 25, y: 14 },
      { id: 3, label: "Note sections", x: 40, y: 50 },
      { id: 4, label: "Save options", x: 90, y: 4 },
    ]
  },
  "record-payment": {
    url: "/help/screenshots/invoice-payment-flow.png",
    alt: "Recording a payment in LumeroFlow",
    annotations: [
      { id: 1, label: "Record payment button", x: 92, y: 11 },
      { id: 2, label: "Invoice status", x: 22, y: 16 },
      { id: 3, label: "Invoice details", x: 50, y: 50 },
    ]
  },
  "calendar-booking": {
    url: "/help/screenshots/calendar-overview.png",
    alt: "Calendar view in LumeroFlow",
    annotations: [
      { id: 1, label: "Book button", x: 72, y: 9 },
      { id: 2, label: "Click empty slot", x: 35, y: 35 },
      { id: 3, label: "Practitioner columns", x: 50, y: 17 },
    ]
  },
  "embed-code": {
    url: "/help/screenshots/online-booking-settings.png",
    alt: "Online booking settings with embed code in LumeroFlow",
    annotations: [
      { id: 1, label: "Booking URL", x: 45, y: 42 },
      { id: 2, label: "Copy link buttons", x: 85, y: 42 },
      { id: 3, label: "Page settings", x: 50, y: 55 },
    ]
  },
};

// Quick help tips for contextual help throughout the app
export const CONTEXTUAL_HELP = {
  "availability": {
    text: "Need help setting practitioner availability?",
    articleId: "set-availability"
  },
  "online-booking": {
    text: "Need help adding your booking page to your website?",
    articleId: "embed-booking-page"
  },
  "forms-assignment": {
    text: "Need help assigning forms to appointments?",
    articleId: "assign-forms"
  },
  "patient-forms-pending": {
    text: "Why are forms pending?",
    articleId: "troubleshoot-forms-not-appearing"
  },
  "ai-documentation": {
    text: "Recording or transcription not working?",
    articleId: "troubleshoot-ai-recording"
  },
  "no-booking-slots": {
    text: "Why are no slots showing?",
    articleId: "troubleshoot-no-slots"
  }
};

// Helper function to get articles by category
export const getArticlesByCategory = (categoryId) => {
  return HELP_ARTICLES.filter(a => a.category === categoryId);
};

// Helper function to search articles
export const searchArticles = (query) => {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return HELP_ARTICLES.filter(a => 
    a.title.toLowerCase().includes(q) ||
    a.summary.toLowerCase().includes(q) ||
    a.keywords.some(k => k.toLowerCase().includes(q)) ||
    a.steps.some(s => s.toLowerCase().includes(q))
  );
};

// Helper function to get related articles
export const getRelatedArticles = (articleId) => {
  const article = HELP_ARTICLES.find(a => a.id === articleId);
  if (!article?.relatedArticleIds) return [];
  return article.relatedArticleIds
    .map(id => HELP_ARTICLES.find(a => a.id === id))
    .filter(Boolean);
};

// Helper function to get article by ID
export const getArticleById = (articleId) => {
  return HELP_ARTICLES.find(a => a.id === articleId);
};

// Helper function to get category by ID
export const getCategoryById = (categoryId) => {
  return HELP_CATEGORIES.find(c => c.id === categoryId);
};
